/**
 * Ambush — anchored ambusher (Brief 008 archetype; species: spider on a thread).
 *
 * Hangs at its ceiling anchor. When the player enters horizontal `range` AND is BELOW
 * the anchor, it snapshots the player's Y and drops straight down to that depth, then
 * returns up to the anchor and re-arms. Reuses the bat-swoop state machine from
 * Flyer.ts (HANG → strike → RETURN), constrained to the vertical axis.
 *
 * Sprites: `<variant>_hang` while idle, `<variant>_drop` while moving. (`<variant>_flat`
 * is vendored for set completeness but never shown — an ambusher is un-stompable.)
 *
 * Combat: UN-STOMPABLE — every contact hurts the player (like a moving spike). There is
 * no top-kill; you avoid it, you don't bounce it.
 *
 * Determinism: floor/abs/min/max + sqrt only (sqrt is correctly-rounded, NOT in the
 * transcendental ban). No sin/cos/pow, no random, no Date.now.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_RANGE = 200;       // px — horizontal trigger radius around anchorX
const DEFAULT_DROP_SPEED = 280;  // px/s while dropping
const RETURN_SPEED = 140;        // px/s while climbing back
const DEFAULT_MAX_DROP = 256;    // px — deepest the drop reaches below the anchor

type AmbushState = 'HANG' | 'DROP' | 'RETURN';

export class Ambush implements Entity {
  readonly type = EntityType.AMBUSH;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  readonly spriteFacesLeft = true; // Kenney spider art faces left (symmetric — cosmetic)
  alive = true;

  private variant: string;
  private range: number;
  private dropSpeed: number;
  private maxDrop: number;
  private state: AmbushState = 'HANG';
  private anchorX: number;
  private anchorY: number; // body-top Y at rest
  private targetY = 0;     // body-top Y to drop to (snapshot at trigger)

  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 48, h = 44) {
    const variant = props?.variant;
    this.variant = typeof variant === 'string' && variant.length ? variant : 'spider';
    this.range = typeof props?.range === 'number' ? props.range : DEFAULT_RANGE;
    this.dropSpeed = typeof props?.dropSpeed === 'number' ? props.dropSpeed : DEFAULT_DROP_SPEED;
    this.maxDrop = typeof props?.maxDrop === 'number' ? props.maxDrop : DEFAULT_MAX_DROP;

    // Hang just under the top of the spawn cell (anchored to the ceiling).
    const topY = feetY - h;
    this.anchorX = cx;
    this.anchorY = topY;
    this.body = { x: cx - w / 2, y: topY, w, h };
  }

  update(dt: number, ctx: EntityContext): void {
    const px = ctx.player.body.x + ctx.player.body.w / 2;
    const py = ctx.player.body.y + ctx.player.body.h / 2;

    switch (this.state) {
      case 'HANG': {
        const below = py > this.anchorY;
        if (below && Math.abs(px - this.anchorX) < this.range) {
          // Snapshot drop depth, clamped to maxDrop below the anchor.
          this.targetY = Math.min(ctx.player.body.y, this.anchorY + this.maxDrop);
          this.state = 'DROP';
        }
        break;
      }
      case 'DROP': {
        const step = this.dropSpeed * dt;
        this.body.y += step;
        if (this.body.y >= this.targetY) { this.body.y = this.targetY; this.state = 'RETURN'; }
        break;
      }
      case 'RETURN': {
        const step = RETURN_SPEED * dt;
        this.body.y -= step;
        if (this.body.y <= this.anchorY) { this.body.y = this.anchorY; this.state = 'HANG'; }
        break;
      }
    }
  }

  onPlayerOverlap(ctx: EntityContext): void {
    ctx.hurtPlayer(); // un-stompable — always hurts
  }

  spriteKey(): string {
    return this.state === 'HANG' ? `${this.variant}_hang` : `${this.variant}_drop`;
  }

  visible(): boolean { return true; }
}
